/**
 * AgencySignupConfirm — landing page for the confirmation link in the
 * "email already registered" signup flow. Exchanges the URL token for a
 * freshly-created agency + an auth cookie, then drops the user into the
 * admin dashboard for their new agency.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import { track } from "@/lib/sentry";

export default function AgencySignupConfirm() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", agency: null, error: null });
  // React.StrictMode double-invokes effects in development; the network call
  // burns the one-time token on first run and rejects the second. Guard with
  // a ref so the redemption fires at most once for this mount.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/agencies/signup/confirm", { token });
        track("signup_confirmed", { agency_id: data?.agency?.id });
        setState({ status: "success", agency: data.agency, error: null });
        // Drop them straight into their new agency's admin dashboard.
        setTimeout(() => {
          window.location.href = "/admin?onboarding=1";
        }, 1600);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
        setState({ status: "error", agency: null, error: msg || "This confirmation link is invalid or has expired." });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-clinical-bg flex flex-col" data-testid="signup-confirm-page">
      <header className="border-b border-clinical-border/60 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center">
          <Link to="/"><AfyanexLogo markSize={28} /></Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md card-surface p-10 text-center rounded-2xl shadow-[0_8px_30px_rgba(11,21,48,0.06)]">
          {state.status === "loading" && (
            <div data-testid="confirm-loading">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-clinical-bg">
                <Loader2 className="h-7 w-7 text-clinical-primary animate-spin" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
                Confirming your new agency…
              </h2>
              <p className="mt-2 text-sm text-clinical-subtext">
                One moment while we set everything up.
              </p>
            </div>
          )}

          {state.status === "success" && (
            <div data-testid="confirm-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <Check className="h-7 w-7" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
                {state.agency?.name} is live.
              </h2>
              <p className="mt-2 text-sm text-clinical-subtext">
                You&apos;re signed in. Heading to your dashboard…
              </p>
              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-clinical-subtext">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Launching onboarding…
              </div>
            </div>
          )}

          {state.status === "error" && (
            <div data-testid="confirm-error">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
                We couldn&apos;t confirm this link
              </h2>
              <p className="mt-2 text-sm text-clinical-subtext">{state.error}</p>
              <Link
                to="/agency/signup"
                className="btn-primary mt-6 inline-flex"
                data-testid="confirm-retry-link"
              >
                Start a new signup
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
